import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { 
  RotateCcw, 
  Brain, 
  Heart, 
  Snowflake, 
  Zap, 
  Shield, 
  Eye, 
  Hand,
  Timer,
  Wind,
  Repeat
} from 'lucide-react';

interface ConditionSpecificToolsProps {
  user: any;
  condition: string;
  onBack?: () => void;
}

export const ConditionSpecificTools = ({ user, condition, onBack }: ConditionSpecificToolsProps) => {
  const [activeExercise, setActiveExercise] = useState<string | null>(null);
  const [step, setStep] = useState(0);
  const [timer, setTimer] = useState(0);

  const conditionData = {
    ptsd: {
      title: 'PTSD & Trauma Support',
      subtitle: 'Grounding techniques for flashbacks and dissociation',
      color: 'border-primary/20 bg-gradient-therapeutic',
      tools: [
        {
          id: 'orientation',
          title: 'Orientation Exercise',
          description: 'Ground yourself in time and place during flashbacks',
          icon: <Eye className="h-5 w-5" />,
          duration: '2-3 minutes'
        },
        {
          id: 'butterfly-hug',
          title: 'Butterfly Hug (Bilateral)',
          description: 'Self-administered bilateral stimulation for calming',
          icon: <Heart className="h-5 w-5" />,
          duration: '3-5 minutes'
        },
        {
          id: 'tipp-trauma',
          title: 'TIPP for Trauma',
          description: 'Temperature, Intensive exercise, Paced breathing, Paired muscles',
          icon: <Zap className="h-5 w-5" />,
          duration: '5-10 minutes'
        },
        {
          id: 'safe-place',
          title: 'Safe Place Visualisation',
          description: 'Create a mental sanctuary for when you feel unsafe',
          icon: <Shield className="h-5 w-5" />,
          duration: '5-8 minutes'
        }
      ]
    },
    bpd: {
      title: 'Emotional Regulation (BPD)',
      subtitle: 'Distress tolerance and emotion regulation skills',
      color: 'border-secondary/20 bg-secondary/10',
      tools: [
        {
          id: 'tipp-emotion',
          title: 'TIPP for Intense Emotions',
          description: 'Rapid nervous system reset for emotional overwhelm',
          icon: <Snowflake className="h-5 w-5" />,
          duration: '2-5 minutes'
        },
        {
          id: 'opposite-action',
          title: 'Opposite Action',
          description: 'Do the opposite of what your emotion urges you to do',
          icon: <Repeat className="h-5 w-5" />,
          duration: '3-10 minutes'
        },
        {
          id: 'self-soothe',
          title: '5-Sense Self-Soothing',
          description: 'Comfort yourself using all five senses',
          icon: <Hand className="h-5 w-5" />,
          duration: '5-15 minutes'
        },
        {
          id: 'mindful-emotion',
          title: 'Mindful Observation',
          description: 'Observe emotions without being overwhelmed by them',
          icon: <Brain className="h-5 w-5" />,
          duration: '3-5 minutes'
        }
      ]
    },
    ocd: {
      title: 'OCD Management',
      subtitle: 'Response prevention and uncertainty tolerance',
      color: 'border-soft-accent/20 bg-soft-accent/10',
      tools: [
        {
          id: 'response-prevention',
          title: 'Response Prevention',
          description: 'Delay and resist compulsions step by step',
          icon: <Timer className="h-5 w-5" />,
          duration: '10-30 minutes'
        },
        {
          id: 'uncertainty-training',
          title: 'Uncertainty Practice',
          description: 'Learn to sit with "maybe" and not knowing',
          icon: <Brain className="h-5 w-5" />,
          duration: '5-10 minutes'
        },
        {
          id: 'externalize-ocd',
          title: 'Name the OCD',
          description: 'Separate yourself from OCD thoughts and urges',
          icon: <Eye className="h-5 w-5" />,
          duration: '2-5 minutes'
        },
        {
          id: 'mindful-exposure',
          title: 'Mindful Exposure',
          description: 'Face triggers with mindful awareness',
          icon: <Shield className="h-5 w-5" />,
          duration: '5-15 minutes'
        }
      ]
    },
    selfharm: {
      title: 'Self-Harm Alternatives',
      subtitle: 'Safe ways to cope with urges to hurt yourself',
      color: 'border-primary/20 bg-gradient-therapeutic',
      tools: [
        {
          id: 'ice-alternative',
          title: 'Ice Cube Technique',
          description: 'Hold ice cubes for intense sensation without harm',
          icon: <Snowflake className="h-5 w-5" />,
          duration: '1-2 minutes'
        },
        {
          id: 'drawing-skin',
          title: 'Red Pen Drawing',
          description: 'Draw on skin with washable red marker',
          icon: <Hand className="h-5 w-5" />,
          duration: '2-5 minutes'
        },
        {
          id: 'intense-exercise',
          title: 'Intense Movement',
          description: 'Channel energy into safe physical activity',
          icon: <Zap className="h-5 w-5" />,
          duration: '5-15 minutes'
        },
        {
          id: 'sensory-alternatives',
          title: 'Strong Sensations',
          description: 'Use safe but strong sensory experiences',
          icon: <Brain className="h-5 w-5" />,
          duration: '1-3 minutes'
        }
      ]
    }
  };

  const currentCondition = conditionData[condition];

  if (activeExercise === 'orientation') {
    return <OrientationExercise onReset={() => setActiveExercise(null)} />;
  }

  if (activeExercise === 'butterfly-hug') {
    return <ButterflyHugExercise onReset={() => setActiveExercise(null)} />;
  }

  if (activeExercise === 'tipp-trauma' || activeExercise === 'tipp-emotion') {
    return <TippExercise onReset={() => setActiveExercise(null)} variant={condition} />;
  }

  if (activeExercise === 'ice-alternative') {
    return <IceAlternativeGuide onReset={() => setActiveExercise(null)} />;
  }

  if (activeExercise === 'response-prevention') {
    return <ResponsePreventionGuide onReset={() => setActiveExercise(null)} />;
  }

  if (activeExercise === 'opposite-action') {
    return <OppositeActionGuide onReset={() => setActiveExercise(null)} />;
  }

  return (
    <div className="space-y-6">
      <Card className={currentCondition.color}>
        <CardHeader>
          <div className="flex justify-between items-center">
            <div>
              <CardTitle className="text-primary">{currentCondition.title}</CardTitle>
              <p className="text-muted-foreground mt-1">{currentCondition.subtitle}</p>
            </div>
            <Button onClick={onBack} variant="outline" size="sm">
              <RotateCcw className="h-4 w-4 mr-2" />
              Back
            </Button>
          </div>
        </CardHeader>
      </Card>

      <div className="grid gap-4">
        {currentCondition.tools.map((tool) => (
          <Card key={tool.id} className="cursor-pointer hover:shadow-md transition-shadow">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-start gap-3">
                  <div className="text-primary mt-1">{tool.icon}</div>
                  <div>
                    <h3 className="font-semibold">{tool.title}</h3>
                    <p className="text-muted-foreground text-sm">{tool.description}</p>
                    <Badge variant="secondary" className="mt-2">{tool.duration}</Badge>
                  </div>
                </div>
                <Button
                  onClick={() => setActiveExercise(tool.id)}
                  className="bg-primary hover:bg-primary/90"
                >
                  Start
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card className="bg-primary/5 border-primary/20">
        <CardContent className="p-4">
          <p className="text-sm text-primary text-center">
            <strong>Remember:</strong> These are evidence-based techniques used by therapists worldwide. 
            You're using tools that have helped millions of people cope with these exact challenges.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

// Individual Exercise Components
const OrientationExercise = ({ onReset }: { onReset: () => void }) => {
  const [step, setStep] = useState(0);
  const steps = [
    "Look around and say out loud: 'I am in [current location]'",
    "Say the current date and time: 'Today is [date] and it's [time]'", 
    "Name 3 objects you can see right now",
    "Say your full name and age out loud",
    "Take 5 deep breaths and remind yourself: 'I am safe now'"
  ];

  return (
    <Card className="border-primary/20 bg-gradient-therapeutic">
      <CardHeader>
        <div className="flex justify-between items-center">
          <CardTitle className="text-primary">Orientation Exercise</CardTitle>
          <Button onClick={onReset} variant="outline" size="sm">
            <RotateCcw className="h-4 w-4 mr-2" />
            Back
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="text-center">
          <div className="text-primary text-2xl font-bold mb-2">Step {step + 1} of {steps.length}</div>
          <Progress value={((step + 1) / steps.length) * 100} className="mb-4" />
        </div>
        
        <div className="bg-background p-6 rounded-lg border border-primary/20">
          <p className="text-lg text-center">{steps[step]}</p>
        </div>

        <div className="flex gap-2 justify-center">
          {step > 0 && (
            <Button onClick={() => setStep(step - 1)} variant="outline">
              Previous
            </Button>
          )}
          {step < steps.length - 1 ? (
            <Button onClick={() => setStep(step + 1)} className="bg-primary">
              Next Step
            </Button>
          ) : (
            <Button onClick={onReset} className="bg-primary">
              Complete
            </Button>
          )}
        </div>

        <div className="bg-primary/10 p-4 rounded-lg">
          <p className="text-primary text-sm text-center">
            <strong>This grounds you in reality during flashbacks by connecting you to the present moment.</strong>
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

const ButterflyHugExercise = ({ onReset }: { onReset: () => void }) => (
  <Card className="border-primary/20 bg-gradient-therapeutic">
    <CardHeader>
      <div className="flex justify-between items-center">
        <CardTitle className="text-primary">Butterfly Hug (Bilateral Stimulation)</CardTitle>
        <Button onClick={onReset} variant="outline" size="sm">
          <RotateCcw className="h-4 w-4 mr-2" />
          Back
        </Button>
      </div>
    </CardHeader>
    <CardContent className="space-y-4">
      <div className="text-center text-6xl mb-4">🤗</div>
      
      <div className="space-y-4">
        <div className="bg-background p-4 rounded-lg border border-primary/20">
          <h3 className="font-semibold text-primary mb-2">1. Cross Your Arms</h3>
          <p className="text-muted-foreground">Cross your arms over your chest, placing hands on opposite shoulders.</p>
        </div>
        
        <div className="bg-background p-4 rounded-lg border border-primary/20">
          <h3 className="font-semibold text-primary mb-2">2. Gentle Tapping</h3>
          <p className="text-muted-foreground">Gently tap your hands alternately on your shoulders - left, right, left, right.</p>
        </div>
        
        <div className="bg-background p-4 rounded-lg border border-primary/20">
          <h3 className="font-semibold text-primary mb-2">3. Breathe and Count</h3>
          <p className="text-muted-foreground">Continue for 30 taps while breathing slowly. Count each tap.</p>
        </div>
        
        <div className="bg-background p-4 rounded-lg border border-primary/20">
          <h3 className="font-semibold text-primary mb-2">4. Notice Changes</h3>
          <p className="text-muted-foreground">After 30 taps, pause and notice how your body feels. Repeat if needed.</p>
        </div>
      </div>

      <div className="bg-primary/10 p-4 rounded-lg">
        <p className="text-primary text-sm">
          <strong>How it helps:</strong> Bilateral stimulation activates both brain hemispheres, helping process trauma and reduce distress.
        </p>
      </div>
    </CardContent>
  </Card>
);

const TippExercise = ({ onReset, variant }: { onReset: () => void; variant: string }) => (
  <Card className="border-secondary/20 bg-secondary/10">
    <CardHeader>
      <div className="flex justify-between items-center">
        <CardTitle className="text-primary">TIPP Technique</CardTitle>
        <Button onClick={onReset} variant="outline" size="sm">
          <RotateCcw className="h-4 w-4 mr-2" />
          Back
        </Button>
      </div>
    </CardHeader>
    <CardContent className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="bg-background p-4 rounded-lg border">
          <h3 className="font-semibold text-foreground mb-2">🧊 Temperature</h3>
          <p className="text-sm text-muted-foreground">Cold water on face, ice cubes, or cold shower</p>
        </div>
        
        <div className="bg-background p-4 rounded-lg border">
          <h3 className="font-semibold text-foreground mb-2">🏃 Intensive Exercise</h3>
          <p className="text-sm text-muted-foreground">Running, jumping jacks, or intense movement for 5-10 min</p>
        </div>
        
        <div className="bg-background p-4 rounded-lg border">
          <h3 className="font-semibold text-foreground mb-2">🫁 Paced Breathing</h3>
          <p className="text-sm text-muted-foreground">Exhale longer than inhale (4 in, 6 out)</p>
        </div>
        
        <div className="bg-background p-4 rounded-lg border">
          <h3 className="font-semibold text-foreground mb-2">💪 Paired Muscle</h3>
          <p className="text-sm text-muted-foreground">Tense and release muscle groups progressively</p>
        </div>
      </div>

      <div className="bg-secondary/10 p-4 rounded-lg">
        <p className="text-subtle text-sm">
          <strong>Use when:</strong> {variant === 'ptsd' ? 'Having flashbacks or feeling disconnected' : 'Emotions feel overwhelming or out of control'}
        </p>
      </div>
    </CardContent>
  </Card>
);

const IceAlternativeGuide = ({ onReset }: { onReset: () => void }) => (
  <Card className="border-primary/20 bg-gradient-therapeutic">
    <CardHeader>
      <div className="flex justify-between items-center">
        <CardTitle className="text-primary">Ice Cube Alternative</CardTitle>
        <Button onClick={onReset} variant="outline" size="sm">
          <RotateCcw className="h-4 w-4 mr-2" />
          Back
        </Button>
      </div>
    </CardHeader>
    <CardContent className="space-y-4">
      <div className="text-center text-6xl text-primary mb-4">🧊</div>
      
      <div className="space-y-4">
        <div className="bg-background p-4 rounded-lg border border-primary/20">
          <h3 className="font-semibold text-primary mb-2">Hold Ice Cubes</h3>
          <p className="text-muted-foreground">Grip ice cubes tightly in your hands for 30-60 seconds. The intense cold provides sensation without harm.</p>
        </div>
        
        <div className="bg-background p-4 rounded-lg border border-primary/20">
          <h3 className="font-semibold text-primary mb-2">Ice on Skin</h3>
          <p className="text-muted-foreground">Press ice against your wrists, temples, or where you feel urges. Move it around to avoid frostbite.</p>
        </div>
        
        <div className="bg-background p-4 rounded-lg border border-primary/20">
          <h3 className="font-semibold text-primary mb-2">Cold Water</h3>
          <p className="text-muted-foreground">Plunge hands/wrists in very cold water for 30-60 seconds.</p>
        </div>
      </div>

      <div className="bg-primary/10 p-4 rounded-lg">
        <p className="text-primary text-sm">
          <strong>Why this works:</strong> Provides intense physical sensation that can satisfy the urge for self-harm without causing damage or lasting marks.
        </p>
      </div>
    </CardContent>
  </Card>
);

const ResponsePreventionGuide = ({ onReset }: { onReset: () => void }) => (
  <Card className="border-soft-accent/20 bg-soft-accent/10">
    <CardHeader>
      <div className="flex justify-between items-center">
        <CardTitle className="text-soft-accent">Response Prevention</CardTitle>
        <Button onClick={onReset} variant="outline" size="sm">
          <RotateCcw className="h-4 w-4 mr-2" />
          Back
        </Button>
      </div>
    </CardHeader>
    <CardContent className="space-y-4">
      <div className="bg-background p-4 rounded-lg border border-soft-accent/20">
        <h3 className="font-semibold text-soft-accent mb-2">1. Notice the Urge</h3>
        <p className="text-muted-foreground">"I'm having the urge to [compulsion]" - acknowledge without judging.</p>
      </div>
      
      <div className="bg-background p-4 rounded-lg border border-soft-accent/20">
        <h3 className="font-semibold text-soft-accent mb-2">2. Delay (Don't Avoid)</h3>
        <p className="text-muted-foreground">Set timer for 15 minutes. Say "I can do this after the timer, but not now."</p>
      </div>
      
      <div className="bg-background p-4 rounded-lg border border-soft-accent/20">
        <h3 className="font-semibold text-soft-accent mb-2">3. Distract Mindfully</h3>
        <p className="text-muted-foreground">Do something engaging but don't try to eliminate the anxiety - let it be there.</p>
      </div>
      
      <div className="bg-background p-4 rounded-lg border border-soft-accent/20">
        <h3 className="font-semibold text-soft-accent mb-2">4. Increase Delay</h3>
        <p className="text-muted-foreground">Each time, try to delay a little longer. Even 1 extra minute is progress.</p>
      </div>

      <div className="bg-soft-accent/10 p-4 rounded-lg">
        <p className="text-soft-accent text-sm">
          <strong>Remember:</strong> The goal isn't to eliminate anxiety, but to prove you can handle uncertainty and resist compulsions.
        </p>
      </div>
    </CardContent>
  </Card>
);

const OppositeActionGuide = ({ onReset }: { onReset: () => void }) => (
  <Card className="border-secondary/20 bg-secondary/10">
    <CardHeader>
      <div className="flex justify-between items-center">
        <CardTitle className="text-primary">Opposite Action</CardTitle>
        <Button onClick={onReset} variant="outline" size="sm">
          <RotateCcw className="h-4 w-4 mr-2" />
          Back
        </Button>
      </div>
    </CardHeader>
    <CardContent className="space-y-4">
      <div className="grid gap-4">
        <div className="bg-background p-4 rounded-lg border border-secondary/20">
          <h3 className="font-semibold text-foreground mb-2">Anger → Kindness</h3>
          <p className="text-muted-foreground">Do something kind instead of lashing out</p>
        </div>
        
        <div className="bg-background p-4 rounded-lg border border-secondary/20">
          <h3 className="font-semibold text-foreground mb-2">Fear → Approach</h3>
          <p className="text-muted-foreground">Move toward what scares you (safely)</p>
        </div>
        
        <div className="bg-background p-4 rounded-lg border border-secondary/20">
          <h3 className="font-semibold text-foreground mb-2">Shame → Engage</h3>
          <p className="text-muted-foreground">Make eye contact, speak up, connect with others</p>
        </div>
        
        <div className="bg-background p-4 rounded-lg border border-secondary/20">
          <h3 className="font-semibold text-foreground mb-2">Sadness → Activate</h3>
          <p className="text-muted-foreground">Do something energizing or engaging</p>
        </div>
      </div>

      <div className="bg-secondary/10 p-4 rounded-lg">
        <p className="text-subtle text-sm">
          <strong>Key:</strong> Act opposite to what the emotion urges, but do it gently and with self-compassion.
        </p>
      </div>
    </CardContent>
  </Card>
);